import React, { useState } from 'react';
import './TeaserOverlay.scss';
import clsx from 'clsx';
import RichText from '../RichText';

interface ResponsiveValue {
  xs?: string;
  sm?: string;
  md?: string;
  lg?: string;
  xl?: string;
  xxl?: string;
}

interface TeaserOverlayProps extends React.HTMLAttributes<HTMLDivElement> {
  imgSource: string;
  title: string;
  description: string;
  width?: string;
  aspectRatio?: string | ResponsiveValue;
  mainColor?: string;
  altColor?: string;
  borderColor?: string;
  rounded?: boolean;
  separationRatio?: number;
  titleMaxWidth?: string;
}

const TeaserOverlay: React.FC<TeaserOverlayProps> = ({
  imgSource,
  title,
  description,
  width,
  aspectRatio,
  mainColor = '#fff',
  altColor = '#000',
  borderColor = '#000',
  rounded = false,
  separationRatio = 1 / 3,
  titleMaxWidth,
  ...props
}) => {
  const [isHovered, setIsHovered] = useState(false);

  const separationHeight = `${separationRatio * 100}%`;
  const complementaryHeight = `${(1 - separationRatio) * 100}%`;

  const cssVars: Record<string, string> = {
    '--teaser-overlay-bg': isHovered ? mainColor : altColor,
    '--teaser-overlay-color': isHovered ? altColor : mainColor,
    '--teaser-overlay-border': borderColor || 'transparent',
  };
  if (width) cssVars['--teaser-overlay-width'] = width;
  if (titleMaxWidth)
    cssVars['--teaser-overlay-title-max-width'] = titleMaxWidth;

  if (aspectRatio) {
    if (typeof aspectRatio === 'string') {
      cssVars['--teaser-overlay-aspect-ratio'] = aspectRatio;
    } else {
      const bp: (keyof ResponsiveValue)[] = [
        'xs',
        'sm',
        'md',
        'lg',
        'xl',
        'xxl',
      ];
      let last: string | undefined;
      for (const key of bp) {
        const val = aspectRatio[key] || last;
        if (val) {
          cssVars[`--teaser-overlay-aspect-ratio-${key}`] = val;
          last = val;
        }
      }
    }
  }

  const { style: externalStyle, ...restProps } = props;

  return (
    <div
      className={clsx('teaser-overlay', {
        'teaser-overlay__rounded': rounded,
      })}
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      style={{ ...cssVars, ...externalStyle }}
      {...restProps}
    >
      {!isHovered ? (
        <div className="teaser-overlay-front">
          <div
            className="teaser-overlay__section"
            style={{ height: complementaryHeight }}
          >
            <img src={imgSource} alt={title} className="teaser-image" />
          </div>
          <div
            className="teaser-overlay__section teaser-overlay__section--pad-top"
            style={{ height: separationHeight }}
          >
            <h2 className="teaser-title">{title}</h2>
          </div>
        </div>
      ) : (
        <div className="teaser-overlay-back">
          <div
            className="teaser-overlay__section teaser-overlay__section--pad-bottom"
            style={{ height: separationHeight }}
          >
            <h2 className="teaser-title">{title}</h2>
          </div>
          <div
            className="teaser-overlay__section"
            style={{ height: complementaryHeight }}
          >
            <RichText className="teaser-description" content={description} />
          </div>
        </div>
      )}
    </div>
  );
};

export default TeaserOverlay;
